import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import {
  AmenityGroupAvailabilityReturn,
  AmenityGroupMember,
  AmenityGroupReturn,
  ReservationGroupPreviewReturn,
  ReservationRuleViolation,
} from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading, Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import { Button } from '../Components/Catalyst/button';
import { Input } from '../Components/Catalyst/input';
import { Select } from '../Components/Catalyst/select';
import { Checkbox, CheckboxField } from '../Components/Catalyst/checkbox';
import { Field, Label } from '../Components/Catalyst/fieldset';
import HomeSelector from '../Components/Custom/HomeSelector';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { ErrorPage } from '../Components/Custom/ErrorPage';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import { useImpersonating } from '../Hooks/useImpersonating';
import { formatMoney } from '../utils/billingHelpers';
import {
  addMinutesLocal,
  formatDuration,
  formatLocalRange,
  formatLocalTime,
  isFreeAmount,
  showPartySize,
  timesAreIn,
  todayIn,
} from './reservationFormat';
import { depositReviewText, feeLineAmount, groupStartLabel, showFeeBreakdown } from './reservationPhase3';
import { reservationErrorMessage, ruleMessage, violationsFromError } from './ruleMessages';

const card =
  'rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800';

/** Codes the review answers itself, with the agreement checkbox. */
const AGREEMENT_CODES = ['AGREEMENT_REQUIRED', 'AGREEMENT_VERSION_STALE'];

/**
 * "Any court will do" (#640 phase 3). The household picks a time; the
 * server tries the group's amenities in order and takes the first one free
 * then. The preview says which one it would be, and the fee and rules are
 * that amenity's. A limit or a suspension is the household's and stops the
 * booking whichever amenity it would have been.
 */
const GroupBookingPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const impersonating = useImpersonating();
  const { selectedHome, homes, loading: memberLoading } = useMember();
  const homeId = selectedHome?.home_id;

  const [group, setGroup] = useState<AmenityGroupReturn | null>(null);
  const [members, setMembers] = useState<AmenityGroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [date, setDate] = useState('');
  const [grid, setGrid] = useState<AmenityGroupAvailabilityReturn | null>(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [start, setStart] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [partySize, setPartySize] = useState('1');
  const [preview, setPreview] = useState<ReservationGroupPreviewReturn | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [violations, setViolations] = useState<ReservationRuleViolation[]>([]);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Bumped by "Try again" to re-run the group load below.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!homeId || !groupId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    memberReservationService
      .listMyAmenityGroups({ homeId })
      .then((groups) => {
        if (cancelled) return;
        const g = groups.data.groups.find((x) => x.id === groupId) ?? null;
        setGroup(g);
        // Each member carries what a booking page needs of it (lengths,
        // timezone, party size), so the group list is the only read.
        const found = (g?.amenities ?? [])
          .filter((m) => m.is_active)
          .sort((a, b) => a.sort_order - b.sort_order);
        setMembers(found);
        if (!g) setError(homes.length > 1 ? "This group isn't available to the home you've selected." : "We couldn't find that group.");
        if (found[0]) {
          setDate(todayIn(found[0].timezone));
          setDuration(found[0].min_duration_minutes);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(reservationErrorMessage(err, "Couldn't load this group."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [homeId, groupId, homes.length, reloadKey]);

  // The day's start times across the group.
  useEffect(() => {
    if (!homeId || !groupId || !date) return;
    let cancelled = false;
    setGridLoading(true);
    setStart(null);
    setPreview(null);
    memberReservationService
      .getMyAmenityGroupAvailability({ homeId, groupId, date })
      .then(({ data }) => {
        if (!cancelled) setGrid(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setGrid(null);
          setActionError(reservationErrorMessage(err, "Couldn't load the times for that day."));
        }
      })
      .finally(() => {
        if (!cancelled) setGridLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [homeId, groupId, date]);

  // Booking lengths: the strictest common to the group's amenities.
  const lengths = useMemo(() => {
    if (members.length === 0) return [] as number[];
    const min = Math.max(...members.map((m) => m.min_duration_minutes));
    const max = Math.min(...members.map((m) => m.max_duration_minutes));
    const step = Math.max(15, ...members.map((m) => m.slot_increment_minutes));
    const out: number[] = [];
    for (let m = min; m <= max && out.length < 24; m += step) out.push(m);
    return out.length ? out : [min];
  }, [members]);

  const first = members[0] ?? null;
  const party = Math.max(1, parseInt(partySize, 10) || 1);
  const assigned = preview?.amenity_id ? members.find((m) => m.id === preview.amenity_id) ?? null : null;

  const body = (agreementId?: string | null, quotedFee?: string | null) => ({
    start_local: start as string,
    end_local: addMinutesLocal(start as string, duration),
    party_size: party,
    agreement_ids: agreementId ? [agreementId] : null,
    quoted_fee: quotedFee ?? null,
  });

  const runPreview = async (): Promise<void> => {
    if (!homeId || !groupId || !start) return;
    setWorking(true);
    setActionError(null);
    setViolations([]);
    try {
      const { data } = await memberReservationService.previewMyGroupReservation({
        homeId,
        groupId,
        memberGroupReservationCreate: body(),
      });
      setPreview(data);
      setAgreed(false);
    } catch (err) {
      setActionError(reservationErrorMessage(err, "Couldn't check that time."));
    } finally {
      setWorking(false);
    }
  };

  const book = async (): Promise<void> => {
    if (!homeId || !groupId || !start || !preview?.preview) return;
    setWorking(true);
    setActionError(null);
    setViolations([]);
    try {
      const { data } = await memberReservationService.createMyGroupReservation({
        homeId,
        groupId,
        memberGroupReservationCreate: body(preview.preview.agreement?.id ?? null, preview.preview.fee),
      });
      navigate(`/reservations/${data.id}`);
    } catch (err) {
      const refused = violationsFromError(err);
      if (refused) {
        setViolations(refused);
        if (refused.some((v) => v.code === 'QUOTE_CHANGED' || v.code === 'SLOT_CONFLICT')) {
          setPreview(null);
        }
      } else {
        setActionError(reservationErrorMessage(err, "Couldn't make that booking."));
      }
    } finally {
      setWorking(false);
    }
  };

  if (memberLoading) return <LoadingSpinner />;

  // The group preview's own `violations` is the authoritative list; the
  // nested preview's is the same list when an amenity was picked.
  const previewBlockers = (preview?.violations ?? []).filter(
    (v) => AGREEMENT_CODES.indexOf(v.code) === -1
  );
  const agreement = preview?.preview?.agreement ?? null;
  const pending = preview?.preview?.status_if_booked === 'pending';

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="amenities" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Link
        to="/amenities"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:underline dark:text-zinc-400"
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden />
        Amenities
      </Link>

      {homes.length > 1 && (
        <div className="mt-2">
          <HomeSelector />
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : error || !group || !first ? (
        <ErrorPage
          title={error ? "Couldn't load this group" : 'Nothing to book right now'}
          message={error ?? 'This group has nothing to book right now.'}
          onRetry={error ? () => setReloadKey((k) => k + 1) : undefined}
        />
      ) : (
        <div className="mt-4 max-w-2xl space-y-6">
          <div>
            <Heading>Book any of {group.name}</Heading>
            <Text className="mt-1">
              {members.map((m) => m.name).join(', ')} — we'll give you whichever is free.
            </Text>
            {group.description && <Text className="mt-2">{group.description}</Text>}
          </div>

          <section className={card}>
            <Subheading>When</Subheading>
            <Text className="mt-1 text-sm">{timesAreIn(first.timezone)}</Text>
            <div className="mt-4 space-y-4">
              <Field>
                <Label>Day</Label>
                <Input type="date" value={date} min={todayIn(first.timezone)} onChange={(e) => setDate(e.target.value)} />
              </Field>

              {gridLoading ? (
                <LoadingSpinner />
              ) : (
                <div>
                  <div className="text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">Start time</div>
                  {(grid?.starts ?? []).filter((s) => s.status === 'available').length === 0 ? (
                    <Text className="mt-2">Nothing free that day. Try another day.</Text>
                  ) : (
                    <div role="radiogroup" aria-label="Start time" className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {(grid?.starts ?? [])
                        .filter((s) => s.status === 'available')
                        .map((s) => (
                          <button
                            key={s.start_local}
                            type="button"
                            role="radio"
                            aria-checked={start === s.start_local}
                            onClick={() => {
                              setStart(s.start_local);
                              setPreview(null);
                            }}
                            className={clsx(
                              'min-h-[2.75rem] rounded-lg border px-2 py-1 text-sm',
                              start === s.start_local
                                ? 'border-sky-600 bg-sky-50 text-sky-900 dark:border-sky-400 dark:bg-sky-500/10 dark:text-sky-100'
                                : 'border-zinc-300 text-zinc-800 dark:border-zinc-600 dark:text-zinc-200'
                            )}
                          >
                            {groupStartLabel(formatLocalTime(s.start_local), s.available_amenity_ids.length, members.length)}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {start && (
                <>
                  {lengths.length > 1 && (
                    <Field>
                      <Label>How long</Label>
                      <Select
                        value={duration}
                        onChange={(e) => {
                          setDuration(parseInt(e.target.value, 10));
                          setPreview(null);
                        }}
                      >
                        {lengths.map((m) => (
                          <option key={m} value={m}>
                            {formatDuration(m)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  {showPartySize(first) && (
                    <Field>
                      <Label>How many people</Label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={first.max_party_size ?? undefined}
                        value={partySize}
                        onChange={(e) => {
                          setPartySize(e.target.value);
                          setPreview(null);
                        }}
                      />
                    </Field>
                  )}
                  {!preview && (
                    <Button color="sky" className="w-full sm:w-auto" disabled={working} onClick={runPreview}>
                      {working ? 'Checking…' : 'Find one free'}
                    </Button>
                  )}
                </>
              )}
              {actionError && !preview && <ErrorAlert>{actionError}</ErrorAlert>}
            </div>
          </section>

          {preview && start && (
            <section className={card} aria-live="polite">
              <Subheading>{preview.amenity_name ? `You'd get ${preview.amenity_name}` : 'Nothing free then'}</Subheading>
              <Text className="mt-1">{formatLocalRange(start, addMinutesLocal(start, duration))}</Text>

              {preview.preview && (
                <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-6">
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Fee</dt>
                  <dd className="text-zinc-950 dark:text-white">
                    {isFreeAmount(preview.preview.fee)
                      ? 'Free'
                      : `${formatMoney(preview.preview.fee)}${pending ? ', charged only if approved' : ', charged to your account'}`}
                    {showFeeBreakdown(preview.preview.fee_breakdown) && (
                      <ul className="mt-1 space-y-0.5 text-zinc-600 dark:text-zinc-400">
                        {preview.preview.fee_breakdown.map((line, i) => (
                          <li key={`${line.kind}-${i}`} className="flex justify-between gap-4">
                            <span className="min-w-0">{line.label}</span>
                            <span className="shrink-0 tabular-nums">{feeLineAmount(line)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                  {depositReviewText(preview.preview.deposit, pending) && (
                    <>
                      <dt className="font-medium text-zinc-500 dark:text-zinc-400">Deposit</dt>
                      <dd className="text-zinc-950 dark:text-white">{depositReviewText(preview.preview.deposit, pending)}</dd>
                    </>
                  )}
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Approval</dt>
                  <dd className="text-zinc-950 dark:text-white">
                    {pending ? 'Your management company approves requests for this amenity.' : 'Confirmed as soon as you book.'}
                  </dd>
                </dl>
              )}

              {previewBlockers.length > 0 && (
                <ErrorAlert className="mt-3" title="This booking can't go ahead">
                  <ul className="list-disc space-y-1 pl-4">
                    {previewBlockers.map((v) => (
                      <li key={v.code}>{ruleMessage(v.code, assigned ?? first)}</li>
                    ))}
                  </ul>
                </ErrorAlert>
              )}

              {agreement && (
                <div className="mt-3 space-y-3">
                  <div className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg bg-zinc-50 p-3 text-sm text-zinc-800 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700">
                    <div className="mb-2 font-semibold">{agreement.title}</div>
                    {agreement.body}
                  </div>
                  <CheckboxField>
                    <Checkbox color="sky" checked={agreed} onChange={setAgreed} />
                    <Label>I have read and agree to the {agreement.title}</Label>
                  </CheckboxField>
                </div>
              )}

              {violations.length > 0 && (
                <ErrorAlert className="mt-3" title="Couldn't make that booking">
                  <ul className="list-disc space-y-1 pl-4">
                    {violations.map((v) => (
                      <li key={v.code}>{ruleMessage(v.code, assigned ?? first)}</li>
                    ))}
                  </ul>
                </ErrorAlert>
              )}
              <ErrorAlert className="mt-3">{actionError}</ErrorAlert>
              {impersonating && (
                <Text className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
                  This is a read-only view: you can check which one is free and the price, but not book.
                </Text>
              )}

              <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row">
                <Button outline onClick={() => setPreview(null)}>
                  Back
                </Button>
                <Button
                  color="sky"
                  onClick={book}
                  disabled={
                    working ||
                    impersonating ||
                    !preview.preview ||
                    previewBlockers.length > 0 ||
                    (!!agreement && !agreed)
                  }
                >
                  {working ? 'Booking…' : pending ? 'Send request' : 'Book'}
                </Button>
              </div>
            </section>
          )}
        </div>
      )}
    </SidebarLayout>
  );
};

export default GroupBookingPage;
